import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, Clock, Users, CheckCircle, AlertCircle, LogOut, Shield, DollarSign, X, Settings, Plus, CheckCircle2, XCircle, Moon, Sun, MessageSquare, XOctagon, Send } from 'lucide-react';

// ============================================
// FIREBASE IMPORTS
// ============================================
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, doc, getDoc, setDoc, updateDoc, query, where, onSnapshot, getDocs, orderBy, limit, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

// ============================================
// FIREBASE CONFIG
// ============================================
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// ============================================
// CONSTANTS
// ============================================
const RAZORPAY_KEY = "rzp_test_YOUR_KEY_HERE";
const ADMIN_EMAIL = "snehitraj2006@gmail.com";

const SEMESTERS = [
  'Semester 1', 'Semester 2', 'Semester 3', 'Semester 4',
  'Semester 5', 'Semester 6', 'Semester 7', 'Semester 8'
];

const SEMESTER_PERIODS = [
  { id: 'jan-feb', label: 'Jan - Feb End', endMonth: 1, endDay: 28 },
  { id: 'mar-may', label: 'March - Mid May', endMonth: 4, endDay: 15 },
  { id: 'june', label: 'June', endMonth: 5, endDay: 30 },
  { id: 'july', label: 'July', endMonth: 6, endDay: 31 },
  { id: 'aug-sep', label: 'August - Sept End', endMonth: 8, endDay: 30 },
  { id: 'oct-dec', label: 'October - Mid Dec', endMonth: 11, endDay: 15 }
];

const PRICE_PER_SEMESTER = 25;

const AVAILABLE_COURSES = [
  { code: 'CS101', name: 'Data Structures', professor: 'Prof. Sharma' },
  { code: 'CS102', name: 'Operating Systems', professor: 'Prof. Kumar' },
  { code: 'CS103', name: 'Computer Networks', professor: 'Prof. Gupta' },
  { code: 'CS104', name: 'Database Systems', professor: 'Prof. Patel' },
  { code: 'CS105', name: 'Web Development', professor: 'Prof. Singh' },
  { code: 'CS106', name: 'Machine Learning', professor: 'Prof. Reddy' },
  { code: 'CS107', name: 'Software Engineering', professor: 'Prof. Mehta' },
  { code: 'CS108', name: 'Algorithms', professor: 'Prof. Joshi' }
];

const SCHEDULE_MAP = {
  'CS101': [{ day: 3, time: '09:00 AM' }, { day: 4, time: '09:00 AM' }],
  'CS102': [{ day: 3, time: '11:00 AM' }, { day: 4, time: '11:00 AM' }],
  'CS103': [{ day: 3, time: '02:00 PM' }, { day: 5, time: '02:00 PM' }],
  'CS104': [{ day: 3, time: '09:00 AM' }, { day: 4, time: '02:00 PM' }],
  'CS105': [{ day: 3, time: '11:00 AM' }, { day: 5, time: '11:00 AM' }],
  'CS106': [{ day: 1, time: '11:00 AM' }, { day: 4, time: '03:00 PM' }],
  'CS107': [{ day: 2, time: '02:00 PM' }, { day: 5, time: '09:00 AM' }],
  'CS108': [{ day: 5, time: '03:00 PM' }],
};

// ============================================
// UTILITY: GET IST TIME - FIXED VERSION
// ============================================

const getLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ============================================
// UTILITY: SAFE PHOTO URL - FIXED VERSION
// ============================================

const getSafePhotoURL = (url, name = 'User') => {
  // Handle all falsy values and string "null"
  if (!url || url === '' || url === 'null' || url === 'undefined') {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true`;
  }
  
  // Validate URL format
  try {
    const urlObj = new URL(url);
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true`;
    }
    return url;
  } catch {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true`;
  }
};

// ============================================
// UTILITY: GET PROPER PHOTO URL FROM AUTH
// ============================================
const getProperPhotoURL = (user) => {
  // Try to get photoURL from providerData first (most up-to-date)
  if (user.providerData && user.providerData.length > 0) {
    for (const profile of user.providerData) {
      if (profile.photoURL) {
        return profile.photoURL;
      }
    }
  }
  // Fall back to user.photoURL
  return user.photoURL || null;
};

// ============================================
// MAIN APP COMPONENT
// ============================================
const AttendanceTracker = () => {
  // State
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('login');
  const [todaySessions, setTodaySessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [voteData, setVoteData] = useState({
    method: '',
    timeFrom: '',
    timeTo: '',
    room: ''
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showCourseSetup, setShowCourseSetup] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [showReportExtraClass, setShowReportExtraClass] = useState(false);
  const [extraClassData, setExtraClassData] = useState({
    courseCode: '',
    courseName: '',
    professor: '',
    time: '',
    room: ''
  });
  const [pendingExtraClasses, setPendingExtraClasses] = useState([]);
  const [timeError, setTimeError] = useState('');
  const [darkMode, setDarkMode] = useState(true);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedSessionForNotes, setSelectedSessionForNotes] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  // ============================================
  // DARK MODE PERSISTENCE
  // ============================================
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode === null) {
      setDarkMode(true);
      localStorage.setItem('darkMode', 'true');
      document.documentElement.classList.add('dark');
    } else {
      const isDark = savedDarkMode === 'true';
      setDarkMode(isDark);
      if (isDark) {
        document.documentElement.classList.add('dark');
      }
    }
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('darkMode', newMode.toString());
      if (newMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newMode;
    });
  }, []);

  // ============================================
  // LOAD USER DATA
  // ============================================
  const loadUserData = useCallback(async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);

        if (data.isBlocked) {
          alert('Your account has been blocked for violating community guidelines. Please contact support.');
          await signOut(auth);
          return;
        }

        if (!data.semester || !data.courses || data.courses.length === 0) {
          setShowCourseSetup(true);
          setCurrentView('course-setup');
          return;
        }

        if (data.subscriptionStatus === 'active') {
          const today = new Date();
          const expiry = new Date(data.subscriptionExpiry);
          if (today > expiry) {
            await updateDoc(doc(db, 'users', uid), {
              subscriptionStatus: 'expired'
            });
            setCurrentView('payment');
          } else {
            setCurrentView('dashboard');
          }
        } else if (data.subscriptionStatus === 'trial') {
          const today = new Date();
          const trialEnd = new Date(data.trialEndsAt);
          if (today > trialEnd) {
            await updateDoc(doc(db, 'users', uid), {
              subscriptionStatus: 'expired'
            });
            setCurrentView('payment');
          } else {
            setCurrentView('dashboard');
          }
        } else {
          setCurrentView('payment');
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, []);

  // ============================================
  // AUTH LISTENER
  // ============================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await loadUserData(user.uid);
      } else {
        setCurrentUser(null);
        setUserData(null);
        setCurrentView('login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [loadUserData]);

  // ============================================
  // GOOGLE SIGN IN - FIXED PHOTO URL
  // ============================================
  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Get proper photo URL from providerData
      const properPhotoURL = getProperPhotoURL(user);

      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists()) {
        const trialEnd = new Date();
        trialEnd.setDate(trialEnd.getDate() + 3);

        const trialEndLocal = `${trialEnd.getFullYear()}-${String(trialEnd.getMonth() + 1).padStart(2, '0')}-${String(trialEnd.getDate()).padStart(2, '0')}`;

        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          name: user.displayName || 'User',
          email: user.email,
          photoURL: properPhotoURL, // Use proper photo URL
          courses: [],
          semester: '',
          subscriptionStatus: 'trial',
          trialEndsAt: trialEndLocal,
          subscriptionExpiry: null,
          isBlocked: false,
          isAdmin: false,
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString()
        });

        setShowCourseSetup(true);
        setCurrentView('course-setup');
      } else {
        // Update photo URL if it changed
        const currentData = userDoc.data();
        if (currentData.photoURL !== properPhotoURL) {
          await updateDoc(doc(db, 'users', user.uid), {
            photoURL: properPhotoURL,
            lastLoginAt: new Date().toISOString()
          });
        } else {
          await updateDoc(doc(db, 'users', user.uid), {
            lastLoginAt: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Sign in failed. Please try again.');
    }
  };

  // ============================================
  // SIGN OUT
  // ============================================
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setCurrentView('login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // ============================================
  // SAVE COURSES
  // ============================================
  const handleSaveCourses = async () => {
    if (selectedCourses.length === 0) {
      alert('Please select at least one course');
      return;
    }

    if (!selectedSemester) {
      alert('Please select your semester');
      return;
    }

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        courses: selectedCourses,
        semester: selectedSemester
      });

      await loadUserData(currentUser.uid);
      setShowCourseSetup(false);
    } catch (error) {
      console.error('Error saving courses:', error);
      alert('Failed to save courses. Please try again.');
    }
  };

// ============================================
// OPTIMIZED: getPreviousConsensus (With Composite Index)
// ============================================
  const getPreviousConsensus = async (courseCode, scheduleTime) => {
    try {
      const today = getLocalDate();
      
      // This query now works with the composite index!
      const q = query(
        collection(db, 'sessions'),
        where('courseCode', '==', courseCode),
        where('time', '==', scheduleTime),
        where('date', '<', today), // ✅ Only get past sessions
        orderBy('date', 'desc'),   // ✅ Most recent first
        limit(1)                   // ✅ Only get the latest
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }
      
      const lastSession = snapshot.docs[0].data();
      
      if (lastSession.consensus) {
        console.log(`Found previous consensus for ${courseCode} at ${scheduleTime}:`, lastSession.consensus);
        return lastSession.consensus;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting previous consensus:', error);
      
      // If index doesn't exist yet, show helpful error
      if (error.code === 'failed-precondition') {
        console.error('⚠️ COMPOSITE INDEX REQUIRED - Check console for link to create it');
      }
      
      return null;
    }
  };
  // ============================================
  // LOAD SESSIONS WITH REAL-TIME UPDATES
  // ============================================
  useEffect(() => {
    if (!userData || !userData.courses || userData.courses.length === 0) return;

    let unsubscribe = () => {};

    const loadSessionsWithPreviousConsensus = async () => {
      const today = getLocalDate();
      const dayOfWeek = new Date().getDay();
      const placeholderSessions = [];

      for (const courseCode of userData.courses) {
        const schedule = SCHEDULE_MAP[courseCode] || [];
        const todaySchedule = schedule.filter(s => s.day === dayOfWeek);

        for (const scheduleItem of todaySchedule) {
          const course = AVAILABLE_COURSES.find(c => c.code === courseCode);
          if (course) {
            const previousConsensus = await getPreviousConsensus(courseCode, scheduleItem.time);
            
            placeholderSessions.push({
              id: `${courseCode}_${today}_${scheduleItem.time.replace(/[^0-9]/g, '')}`,
              courseCode,
              courseName: course.name,
              time: scheduleItem.time,
              professor: course.professor,
              room: 'TBD',
              date: today,
              votes: [],
              consensus: previousConsensus,
              isExtraClass: false,
              isPreviousConsensus: !!previousConsensus
            });
          }
        }
      }

      // Subscribe to real-time updates
      const q = query(
        collection(db, 'sessions'),
        where('date', '==', today)
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        const sessionsFromDB = [];
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          if (userData.courses.includes(data.courseCode) || 
              (data.isExtraClass && userData.courses.includes(data.courseCode))) {
            sessionsFromDB.push({
              id: docSnap.id,
              ...data,
              isPreviousConsensus: false
            });
          }
        });

        // ✅ NEW IMPROVED MERGE LOGIC
        const mergedSessions = [];
        
        // Process each placeholder
        for (const placeholder of placeholderSessions) {
          const dbSession = sessionsFromDB.find(s => s.id === placeholder.id);
          
          if (dbSession) {
            // Session exists in Firestore
            if (!dbSession.votes || dbSession.votes.length === 0) {
              // No votes today - show previous consensus
              mergedSessions.push({
                ...dbSession,
                consensus: placeholder.consensus,
                isPreviousConsensus: !!placeholder.consensus
              });
            } else {
              // Has votes today - show current data
              mergedSessions.push(dbSession);
            }
          } else {
            // No Firestore session - use placeholder
            mergedSessions.push(placeholder);
          }
        }
        
        // Add extra classes
        for (const dbSession of sessionsFromDB) {
          if (dbSession.isExtraClass && !mergedSessions.find(s => s.id === dbSession.id)) {
            mergedSessions.push(dbSession);
          }
        }
        
        // Sort by time
        mergedSessions.sort((a, b) => {
          const timeA = a.time.replace(/[^0-9]/g, '');
          const timeB = b.time.replace(/[^0-9]/g, '');
          return timeA.localeCompare(timeB);
        });

        setTodaySessions(mergedSessions);
      });
    };

    loadSessionsWithPreviousConsensus();

    return () => unsubscribe();
  }, [userData]);

  // ============================================
  // NOTES REAL-TIME LISTENER - FIXED VERSION
  // ============================================
  useEffect(() => {
    if (!selectedSessionForNotes) {
      setNotes([]);
      return;
    }

    // FIXED: Query without orderBy to avoid composite index requirement
    const notesQuery = query(
      collection(db, 'notes'),
      where('courseCode', '==', selectedSessionForNotes.courseCode)
    );

    const unsubscribe = onSnapshot(notesQuery, (snapshot) => {
      const loadedNotes = [];
      snapshot.forEach(docSnap => {
        loadedNotes.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      
      // Sort in memory instead of in query
      loadedNotes.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // Newest first
      });
      
      setNotes(loadedNotes);
    }, (error) => {
      console.error('Error loading notes:', error);
      setNotes([]);
    });

    return () => unsubscribe();
  }, [selectedSessionForNotes]);

  // ============================================
  // PENDING EXTRA CLASSES LISTENER
  // ============================================
  useEffect(() => {
    if (!userData?.isAdmin) return;

    const q = query(
      collection(db, 'extra_classes'),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const pending = [];
      snapshot.forEach(doc => {
        pending.push({ id: doc.id, ...doc.data() });
      });
      setPendingExtraClasses(pending);
    });

    return () => unsubscribe();
  }, [userData?.isAdmin]);

  // ============================================
  // ADD NOTE - FIXED VERSION
  // ============================================
  const handleAddNote = async () => {
    const trimmedNote = newNote.trim();
    
    if (!trimmedNote) {
      alert('Please enter a note');
      return;
    }
    
    if (trimmedNote.length > 500) {
      alert('Note must be less than 500 characters');
      return;
    }
    
    const sanitizedNote = trimmedNote.replace(/<[^>]*>/g, '');
    
    try {
      await addDoc(collection(db, 'notes'), {
        courseCode: selectedSessionForNotes.courseCode,
        courseName: selectedSessionForNotes.courseName,
        note: sanitizedNote,
        userId: currentUser.uid,
        userName: userData.name || 'User',
        userPhoto: userData.photoURL || '',
        createdAt: new Date().toISOString() // Use ISO string for consistency
      });

      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note. Please try again.');
    }
  };

  // ============================================
  // DELETE NOTE
  // ============================================
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await deleteDoc(doc(db, 'notes', noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note.');
    }
  };

  // ============================================
  // TIME VALIDATION
  // ============================================
  const validateTime = (from, to) => {
    if (!from || !to) return true;

    const fromMinutes = parseInt(from.split(':')[0]) * 60 + parseInt(from.split(':')[1]);
    const toMinutes = parseInt(to.split(':')[0]) * 60 + parseInt(to.split(':')[1]);

    if (toMinutes <= fromMinutes) {
      setTimeError('"To" time must be later than "From" time');
      return false;
    }

    setTimeError('');
    return true;
  };

  // ============================================
  // CALCULATE CONSENSUS
  // ============================================
  const calculateConsensus = (votes) => {
    if (!votes || votes.length === 0) return null;

    const voteCombinations = {};
    
    votes.forEach(vote => {
      const key = vote.method === 'cancelled' 
        ? 'cancelled' 
        : `${vote.method}|${vote.timeFrom}|${vote.timeTo}`;
      
      if (!voteCombinations[key]) {
        voteCombinations[key] = {
          method: vote.method,
          timeFrom: vote.timeFrom || '',
          timeTo: vote.timeTo || '',
          count: 0,
          latestTimestamp: vote.timestamp
        };
      }
      voteCombinations[key].count++;
      if (new Date(vote.timestamp) > new Date(voteCombinations[key].latestTimestamp)) {
        voteCombinations[key].latestTimestamp = vote.timestamp;
      }
    });

    const sorted = Object.values(voteCombinations).sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return new Date(b.latestTimestamp) - new Date(a.latestTimestamp);
    });

    const winner = sorted[0];
    return {
      method: winner.method,
      timeFrom: winner.timeFrom,
      timeTo: winner.timeTo,
      voteCount: votes.length
    };
  };

  // ============================================
  // SUBMIT VOTE
  // ============================================
  const handleVoteSubmit = async () => {
    if (!voteData.method) {
      alert('Please select an option');
      return;
    }

    if (voteData.method !== 'cancelled') {
      if (!voteData.timeFrom || !voteData.timeTo) {
        alert('Please fill all required fields');
        return;
      }
      if (!validateTime(voteData.timeFrom, voteData.timeTo)) return;
    }

    if (userData.isBlocked) {
      alert('Your account has been blocked for violating community guidelines.');
      return;
    }

    const newVote = {
      userId: currentUser.uid,
      userName: userData.name || 'User',
      photoURL: userData.photoURL || '',
      method: voteData.method,
      timeFrom: voteData.method === 'cancelled' ? '' : voteData.timeFrom,
      timeTo: voteData.method === 'cancelled' ? '' : voteData.timeTo,
      room: voteData.room || selectedSession.room,
      timestamp: new Date().toISOString()
    };

    const today = getLocalDate();
    const sessionId = selectedSession.id || `${selectedSession.courseCode}_${today}_${selectedSession.time.replace(/[^0-9]/g, '')}`;
    const sessionRef = doc(db, 'sessions', sessionId);

    try {
      const sessionDoc = await getDoc(sessionRef);

      if (!sessionDoc.exists()) {
        await setDoc(sessionRef, {
          courseCode: selectedSession.courseCode,
          courseName: selectedSession.courseName,
          date: today,
          time: selectedSession.time,
          professor: selectedSession.professor,
          room: voteData.room || selectedSession.room,
          votes: [newVote],
          consensus: calculateConsensus([newVote]),
          isExtraClass: selectedSession.isExtraClass || false,
          createdAt: new Date().toISOString()
        });
      } else {
        const existingVotes = sessionDoc.data().votes || [];
        const filteredVotes = existingVotes.filter(v => v.userId !== currentUser.uid);
        const updatedVotes = [...filteredVotes, newVote];

        const newConsensus = calculateConsensus(updatedVotes);

        await updateDoc(sessionRef, {
          votes: updatedVotes,
          room: voteData.room || sessionDoc.data().room,
          consensus: newConsensus,
          updatedAt: new Date().toISOString()
        });
      }

      setSelectedSession(null);
      setVoteData({ method: '', timeFrom: '', timeTo: '', room: '' });
      setTimeError('');
      alert('Vote submitted successfully! 🎉');
    } catch (error) {
      console.error('Error submitting vote:', error);
      alert('Failed to submit vote. Please try again.');
    }
  };

  // ============================================
  // REMOVE VOTE
  // ============================================
  const handleRemoveVote = async (session) => {
    if (!window.confirm('Are you sure you want to remove your vote?')) return;

    const today = getLocalDate();
    const sessionId = session.id || `${session.courseCode}_${today}_${session.time.replace(/[^0-9]/g, '')}`;
    const sessionRef = doc(db, 'sessions', sessionId);

    try {
      const sessionDoc = await getDoc(sessionRef);
      if (sessionDoc.exists()) {
        const existingVotes = sessionDoc.data().votes || [];
        const updatedVotes = existingVotes.filter(v => v.userId !== currentUser.uid);

        if (updatedVotes.length === 0) {
          await updateDoc(sessionRef, {
            votes: [],
            consensus: null,
            updatedAt: new Date().toISOString()
          });
        } else {
          const newConsensus = calculateConsensus(updatedVotes);

          await updateDoc(sessionRef, {
            votes: updatedVotes,
            consensus: newConsensus,
            updatedAt: new Date().toISOString()
          });
        }

        alert('Vote removed successfully!');
      }
    } catch (error) {
      console.error('Error removing vote:', error);
      alert('Failed to remove vote. Please try again.');
    }
  };

  // ============================================
  // REPORT EXTRA CLASS
  // ============================================
  const handleReportExtraClass = async () => {
    if (!extraClassData.courseCode || !extraClassData.time) {
      alert('Please select a course and time');
      return;
    }

    const today = getLocalDate();

    try {
      const extraClassRef = doc(collection(db, 'extra_classes'));
      await setDoc(extraClassRef, {
        courseCode: extraClassData.courseCode,
        courseName: extraClassData.courseName,
        professor: extraClassData.professor || 'TBD',
        time: extraClassData.time,
        room: extraClassData.room || 'TBD',
        date: today,
        reportedBy: currentUser.uid,
        reportedByName: userData.name,
        reportedByEmail: userData.email,
        status: 'pending',
        createdAt: new Date().toISOString()
      });

      setShowReportExtraClass(false);
      setExtraClassData({
        courseCode: '',
        courseName: '',
        professor: '',
        time: '',
        room: ''
      });

      alert('Extra class reported! Admin will review it shortly. 🎉');
    } catch (error) {
      console.error('Error reporting extra class:', error);
      alert('Failed to report extra class. Please try again.');
    }
  };

  // ============================================
  // ADMIN: APPROVE/REJECT EXTRA CLASS
  // ============================================
  const handleExtraClassDecision = async (extraClass, approved) => {
    try {
      if (approved) {
        const today = getLocalDate();
        const sessionId = `EXTRA_${extraClass.id}_${today}`;

        await setDoc(doc(db, 'sessions', sessionId), {
          courseCode: extraClass.courseCode,
          courseName: extraClass.courseName,
          date: today,
          time: extraClass.time,
          professor: extraClass.professor,
          room: extraClass.room,
          votes: [],
          consensus: null,
          isExtraClass: true,
          approvedAt: new Date().toISOString()
        });
      }

      await updateDoc(doc(db, 'extra_classes', extraClass.id), {
        status: approved ? 'approved' : 'rejected',
        reviewedAt: new Date().toISOString(),
        reviewedBy: currentUser.uid
      });

      alert(approved ? 'Extra class approved!' : 'Extra class rejected!');
    } catch (error) {
      console.error('Error handling extra class:', error);
      alert('Failed to process request. Please try again.');
    }
  };

  // ============================================
  // RAZORPAY PAYMENT
  // ============================================
  const handlePayment = (semesterPeriod) => {
    const amount = PRICE_PER_SEMESTER * 100;

    const options = {
      key: RAZORPAY_KEY,
      amount: amount,
      currency: 'INR',
      name: 'AttendHub',
      description: `Subscription: ${semesterPeriod.label}`,
      handler: async function (response) {
        try {
          const today = new Date();
          const currentYear = today.getFullYear();
          let expiryYear = currentYear;

          if (semesterPeriod.endMonth < today.getMonth()) {
            expiryYear = currentYear + 1;
          }

          const expiryDate = new Date(expiryYear, semesterPeriod.endMonth, semesterPeriod.endDay);
          const expiryLocal = `${expiryDate.getFullYear()}-${String(expiryDate.getMonth() + 1).padStart(2, '0')}-${String(expiryDate.getDate()).padStart(2, '0')}`;

          await updateDoc(doc(db, 'users', currentUser.uid), {
            subscriptionStatus: 'active',
            subscriptionExpiry: expiryLocal,
            lastPaymentId: response.razorpay_payment_id,
            lastPaymentDate: new Date().toISOString(),
            lastPaymentAmount: PRICE_PER_SEMESTER
          });

          await loadUserData(currentUser.uid);
          setShowPaymentModal(false);
          alert('Payment successful! Your subscription is now active. 🎉');
        } catch (error) {
          console.error('Payment processing error:', error);
          alert('Payment received but failed to update subscription. Please contact support with payment ID: ' + response.razorpay_payment_id);
        }
      },
      prefill: {
        name: userData?.name || '',
        email: userData?.email || ''
      },
      theme: {
        color: '#6366f1'
      },
      modal: {
        ondismiss: function() {
          console.log('Payment cancelled');
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  // ============================================
  // ADMIN: LOAD ALL USERS
  // ============================================
  useEffect(() => {
    if (userData?.isAdmin && currentView === 'admin') {
      const loadUsers = async () => {
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          const users = [];
          usersSnapshot.forEach(doc => {
            users.push({ id: doc.id, ...doc.data() });
          });
          setAllUsers(users);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      };
      loadUsers();
    }
  }, [userData, currentView]);

  // ============================================
  // ADMIN: BLOCK/UNBLOCK USER
  // ============================================
  const handleBlockUser = async (userId, currentlyBlocked) => {
    const reason = currentlyBlocked ? '' : prompt('Enter reason for blocking:');
    if (!currentlyBlocked && !reason) return;

    try {
      await updateDoc(doc(db, 'users', userId), {
        isBlocked: !currentlyBlocked,
        blockedReason: reason || '',
        blockedAt: !currentlyBlocked ? new Date().toISOString() : null,
        blockedBy: !currentlyBlocked ? currentUser.uid : null
      });

      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = [];
      usersSnapshot.forEach(doc => {
        users.push({ id: doc.id, ...doc.data() });
      });
      setAllUsers(users);

      alert(currentlyBlocked ? 'User unblocked successfully' : 'User blocked successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user status');
    }
  };

  // ============================================
  // UTILITY: TIME AGO
  // ============================================
  const getTimeAgo = (timestamp) => {
    const minutes = Math.floor((new Date() - new Date(timestamp)) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 min ago';
    if (minutes < 60) return `${minutes} mins ago`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    return `${Math.floor(hours / 24)} days ago`;
  };

  // ============================================
  // LOADING SCREEN
  // ============================================
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-3 ${
            darkMode ? 'border-indigo-500' : 'border-indigo-600'
          }`}></div>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Loading AttendHub
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // LOGIN SCREEN
  // ============================================
  if (currentView === 'login') {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${
        darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
      }`}>
        <div className="max-w-md w-full">
          <div className="flex justify-end mb-4">
            <button
              onClick={toggleDarkMode}
              className={`p-2.5 rounded-lg transition-colors ${
                darkMode 
                  ? 'bg-[#1C2128] text-gray-400 hover:text-gray-300 hover:bg-[#252C35]' 
                  : 'bg-white text-gray-600 hover:text-gray-800 border border-gray-200'
              }`}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          <div className={`rounded-lg p-8 ${
            darkMode ? 'bg-[#1C2128]' : 'bg-white border border-gray-200'
          }`}>
            <div className="text-center mb-8">
              <img 
                src="/logo192.png" 
                alt="AttendHub" 
                className="w-12 h-12 rounded-lg mx-auto mb-4"
              />
              <h1 className={`text-2xl font-semibold mb-2 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                AttendHub
              </h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Never miss marking attendance again
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className={`flex items-start gap-3 p-3 rounded-lg ${
                darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
              }`}>
                <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`} />
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Track classes and vote on attendance methods
                </p>
              </div>
              <div className={`flex items-start gap-3 p-3 rounded-lg ${
                darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
              }`}>
                <Users className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  darkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Collaborate with classmates in real-time
                </p>
              </div>
              <div className={`flex items-start gap-3 p-3 rounded-lg ${
                darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
              }`}>
                <Shield className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  darkMode ? 'text-purple-400' : 'text-purple-600'
                }`} />
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Transparent voting with real names
                </p>
              </div>
            </div>

            <button
              onClick={handleGoogleSignIn}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-3 ${
                darkMode 
                  ? 'bg-indigo-500 hover:bg-indigo-600 text-white' 
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>

            <p className={`text-xs text-center mt-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              By signing in, you agree to use this tool responsibly.
              Your real name will be visible for transparency.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: COURSE SETUP SCREEN
  // ============================================
  if (currentView === 'course-setup' || showCourseSetup) {
    return (
      <div className={`min-h-screen p-4 ${darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'}`}>
        <div className="max-w-2xl mx-auto py-8">
          <div className="flex justify-end mb-4">
            <button
              onClick={toggleDarkMode}
              className={`p-2.5 rounded-lg transition-colors ${
                darkMode 
                  ? 'bg-[#1C2128] text-gray-400 hover:text-gray-300' 
                  : 'bg-white text-gray-600 hover:text-gray-800 border border-gray-200'
              }`}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-[#1C2128]' : 'bg-white border border-gray-200'
          }`}>
            <h2 className={`text-xl font-semibold mb-2 ${
              darkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {showCourseSetup && userData ? 'Edit Your Courses' : 'Set up your profile'}
            </h2>
            <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Select your semester and courses
            </p>

            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Semester
              </label>
              <select
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
                className={`w-full py-2.5 px-3 rounded-lg border text-sm transition-colors ${
                  darkMode 
                    ? 'bg-[#0F1419] border-gray-700 text-gray-300 focus:border-indigo-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-500'
                } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
              >
                <option value="">Select Semester</option>
                {SEMESTERS.map(sem => (
                  <option key={sem} value={sem}>{sem}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Courses
              </label>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                {AVAILABLE_COURSES.map(course => (
                  <label
                    key={course.code}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedCourses.includes(course.code)
                        ? darkMode
                          ? 'bg-indigo-500/10 border-indigo-500'
                          : 'bg-indigo-50 border-indigo-500'
                        : darkMode
                          ? 'bg-[#0F1419] border-gray-700 hover:border-gray-600'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCourses([...selectedCourses, course.code]);
                        } else {
                          setSelectedCourses(selectedCourses.filter(c => c !== course.code));
                        }
                      }}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${
                        darkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        {course.code} - {course.name}
                      </div>
                      <div className={`text-xs truncate ${
                        darkMode ? 'text-gray-500' : 'text-gray-600'
                      }`}>
                        {course.professor}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={handleSaveCourses}
                disabled={selectedCourses.length === 0 || !selectedSemester}
                className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
                  darkMode 
                    ? 'bg-indigo-500 hover:bg-indigo-600 text-white disabled:bg-gray-700 disabled:text-gray-500' 
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-200 disabled:text-gray-400'
                } disabled:cursor-not-allowed`}
              >
                {showCourseSetup && userData ? 'Save Changes' : 'Continue'}
              </button>

              {showCourseSetup && userData && (
                <button
                  onClick={() => {
                    setShowCourseSetup(false);
                    setSelectedCourses(userData.courses);
                    setSelectedSemester(userData.semester);
                  }}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
                    darkMode 
                      ? 'bg-[#0F1419] hover:bg-[#252C35] text-gray-300 border border-gray-700' 
                      : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                  }`}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: PAYMENT SCREEN
  // ============================================
  if (currentView === 'payment') {
    const isExpired = userData?.subscriptionStatus === 'expired';
    
    return (
      <div className={`min-h-screen p-4 ${darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'}`}>
        <div className="max-w-2xl mx-auto py-8">
          <div className="flex justify-end mb-4">
            <button
              onClick={toggleDarkMode}
              className={`p-2.5 rounded-lg transition-colors ${
                darkMode 
                  ? 'bg-[#1C2128] text-gray-400 hover:text-gray-300' 
                  : 'bg-white text-gray-600 hover:text-gray-800 border border-gray-200'
              }`}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-[#1C2128]' : 'bg-white border border-gray-200'
          }`}>
            <div className="text-center mb-6">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 ${
                darkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'
              }`}>
                <DollarSign className={`w-6 h-6 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {isExpired ? 'Renew Subscription' : 'Choose Your Plan'}
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isExpired ? 'Your subscription has expired' : 'Select the semester period'}
              </p>
            </div>

            <div className={`rounded-lg p-5 mb-6 text-center ${
              darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
            }`}>
              <div className={`text-3xl font-semibold mb-1 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                ₹{PRICE_PER_SEMESTER}
              </div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                per semester period
              </div>
              <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                One-time payment • No auto-renewal
              </div>
            </div>

            <div className="space-y-2">
              {SEMESTER_PERIODS.map(period => (
                <button
                  key={period.id}
                  onClick={() => handlePayment(period)}
                  className={`w-full py-3 px-4 rounded-lg border font-medium transition-colors text-left flex items-center justify-between ${
                    darkMode 
                      ? 'bg-[#0F1419] border-gray-700 text-gray-300 hover:border-gray-600' 
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="text-sm">{period.label}</span>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>→</span>
                </button>
              ))}
            </div>

            {userData?.isAdmin && (
              <button
                onClick={() => setCurrentView('dashboard')}
                className={`w-full mt-4 py-2.5 px-4 rounded-lg font-medium transition-colors ${
                  darkMode 
                    ? 'bg-[#0F1419] text-gray-400 hover:text-gray-300 border border-gray-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Skip (Admin Access)
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: ADMIN PANEL
  // ============================================
  if (currentView === 'admin' && userData?.isAdmin) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto p-4">
          <div className={`rounded-lg overflow-hidden ${
            darkMode ? 'bg-[#1C2128]' : 'bg-white border border-gray-200'
          }`}>
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  <h2 className={`text-lg font-semibold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    Admin Dashboard
                  </h2>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={toggleDarkMode}
                    className={`p-2 rounded-lg transition-colors ${
                      darkMode 
                        ? 'bg-[#0F1419] text-gray-400 hover:text-gray-300' 
                        : 'bg-gray-100 text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => setCurrentView('dashboard')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      darkMode 
                        ? 'bg-indigo-500 hover:bg-indigo-600 text-white' 
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    Back to Dashboard
                  </button>
                </div>
              </div>
            </div>

            {pendingExtraClasses.length > 0 && (
              <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-medium mb-3 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Pending Extra Class Requests ({pendingExtraClasses.length})
                </h3>
                <div className="space-y-2">
                  {pendingExtraClasses.map(extraClass => (
                    <div key={extraClass.id} className={`p-3 rounded-lg border ${
                      darkMode ? 'bg-[#0F1419] border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-medium text-sm truncate ${
                            darkMode ? 'text-gray-200' : 'text-gray-900'
                          }`}>
                            {extraClass.courseName}
                          </h4>
                          <p className={`text-xs truncate ${
                            darkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {extraClass.professor} • {extraClass.time} • Room {extraClass.room}
                          </p>
                          <p className={`text-xs mt-1 ${
                            darkMode ? 'text-gray-500' : 'text-gray-500'
                          }`}>
                            Reported by {extraClass.reportedByName}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleExtraClassDecision(extraClass, true)}
                            className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                            title="Approve"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleExtraClassDecision(extraClass, false)}
                            className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4">
              <h3 className={`text-sm font-medium mb-3 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                All Users ({allUsers.length})
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allUsers.map(user => (
                  <div
                    key={user.id}
                    className={`p-3 rounded-lg border flex items-center justify-between gap-3 ${
                      user.isBlocked 
                        ? darkMode
                          ? 'bg-red-500/10 border-red-500/30'
                          : 'bg-red-50 border-red-200'
                        : darkMode
                          ? 'bg-[#0F1419] border-gray-700'
                          : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <img
                        src={getSafePhotoURL(user?.photoURL, user?.name)}
                        alt={user?.name || 'User'}
                        className="w-8 h-8 rounded-full"
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${
                          darkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>
                          {user.name}
                        </div>
                        <div className={`text-xs truncate ${
                          darkMode ? 'text-gray-500' : 'text-gray-600'
                        }`}>
                          {user.email}
                        </div>
                        {user.isBlocked && (
                          <div className={`text-xs mt-1 ${
                            darkMode ? 'text-red-400' : 'text-red-600'
                          }`}>
                            Blocked: {user.blockedReason}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleBlockUser(user.id, user.isBlocked)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        user.isBlocked
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-red-500 hover:bg-red-600 text-white'
                      }`}
                    >
                      {user.isBlocked ? 'Unblock' : 'Block'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: MAIN DASHBOARD (START)
  // ============================================
  if (currentView === 'dashboard') {
    const today = new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    let statusMessage = '';
    if (userData.subscriptionStatus === 'trial') {
      const daysLeft = Math.ceil((new Date(userData.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24));
      statusMessage = `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left in trial`;
    } else if (userData.subscriptionStatus === 'active') {
      statusMessage = 'Active';
    }

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'}`}>
        {/* ============================================ */}
        {/* HEADER */}
        {/* ============================================ */}
        <div className={`border-b sticky top-0 z-50 backdrop-blur-sm ${
          darkMode 
            ? 'bg-[#1C2128]/95 border-gray-800' 
            : 'bg-white/95 border-gray-200'
        }`}>
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo192.png" 
                  alt="AttendHub" 
                  className="w-8 h-8 rounded-lg"
                />
                <div>
                  <h1 className={`text-base font-semibold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    AttendHub
                  </h1>
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                    {userData.semester}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={toggleDarkMode}
                  className={`p-2 rounded-lg transition-colors ${
                    darkMode 
                      ? 'bg-[#0F1419] text-gray-400 hover:text-gray-300' 
                      : 'bg-gray-100 text-gray-600 hover:text-gray-800'
                  }`}
                  title={darkMode ? 'Light Mode' : 'Dark Mode'}
                >
                  {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>

                {userData?.isAdmin && (
                  <button
                    onClick={() => setCurrentView('admin')}
                    className={`p-2 rounded-lg transition-colors relative ${
                      darkMode 
                        ? 'bg-[#0F1419] text-gray-400 hover:text-gray-300' 
                        : 'bg-gray-100 text-gray-600 hover:text-gray-800'
                    }`}
                    title="Admin Panel"
                  >
                    <Shield className="w-4 h-4" />
                    {pendingExtraClasses.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-semibold rounded-full w-4 h-4 flex items-center justify-center">
                        {pendingExtraClasses.length}
                      </span>
                    )}
                  </button>
                )}
                
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    darkMode 
                      ? 'bg-[#0F1419] text-gray-400 hover:text-gray-300' 
                      : 'bg-gray-100 text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <DollarSign className="w-4 h-4" />
                  {statusMessage}
                </button>

                <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                  darkMode ? 'bg-[#0F1419]' : 'bg-gray-100'
                }`}>
                  <img
                    src={getSafePhotoURL(userData?.photoURL, userData?.name)}
                    alt={userData?.name || 'User'}
                    className="w-5 h-5 rounded-full"
                  />
                  <span className={`text-xs font-medium ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {userData.name}
                  </span>
                </div>

                <button
                  onClick={handleSignOut}
                  className={`p-2 rounded-lg transition-colors ${
                    darkMode 
                      ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' 
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                  title="Sign Out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* MAIN CONTENT */}
        {/* ============================================ */}
        <div className="max-w-6xl mx-auto p-4">

          {/* ============================================ */}
          {/* DATE & ACTIONS */}
          {/* ============================================ */}
          <div className={`rounded-lg p-4 mb-4 ${
            darkMode ? 'bg-[#1C2128]' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className={`text-lg font-semibold mb-1 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  Today's Schedule
                </h2>
                <p className={`text-xs flex items-center gap-1.5 ${
                  darkMode ? 'text-gray-500' : 'text-gray-600'
                }`}>
                  <Clock className="w-3.5 h-3.5" />
                  {today}
                </p>
              </div>
              <button
                onClick={() => setShowReportExtraClass(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  darkMode 
                    ? 'bg-indigo-500 hover:bg-indigo-600 text-white' 
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Report Extra Class</span>
              </button>
            </div>
          </div>

          {/* ============================================ */}
          {/* SESSIONS LIST */}
          {/* ============================================ */}
          {todaySessions.length === 0 ? (
            <div className={`rounded-lg p-12 text-center ${
              darkMode ? 'bg-[#1C2128]' : 'bg-white border border-gray-200'
            }`}>
              <img 
                src="/logo192.png" 
                alt="AttendHub" 
                className="w-12 h-12 rounded-lg mx-auto mb-3"
              />
              <h3 className={`text-lg font-medium mb-1 ${
                darkMode ? 'text-gray-200' : 'text-gray-900'
              }`}>
                No classes scheduled today
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                Enjoy your day off!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {todaySessions.map(session => {
                const userVote = session.votes?.find(v => v.userId === currentUser.uid);
                const hasVotes = session.votes && session.votes.length > 0;

                return (
                  <div
                    key={session.id}
                    className={`rounded-lg p-4 border ${
                      darkMode 
                        ? 'bg-[#1C2128] border-gray-800' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`text-base font-semibold ${
                            darkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {session.courseName}
                          </h3>
                          {session.isExtraClass && (
                            <span className="bg-orange-500/10 text-orange-400 text-xs px-2 py-0.5 rounded font-medium">
                              Extra
                            </span>
                          )}
                        </div>
                        <div className={`flex items-center gap-3 text-xs ${
                          darkMode ? 'text-gray-500' : 'text-gray-600'
                        }`}>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {session.time}
                          </span>
                          <span>•</span>
                          <span>{session.professor}</span>
                          <span>•</span>
                          <span>Room {session.room}</span>
                        </div>
                      </div>

                      {hasVotes && (
                        <div className={`px-2.5 py-1 rounded-lg flex items-center gap-1.5 ${
                          darkMode ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600'
                        }`}>
                          <Users className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium">{session.votes.length}</span>
                        </div>
                      )}
                    </div>

                    {/* CONSENSUS DISPLAY */}
                    {session.consensus && (
                      <div className={`rounded-lg p-3 mb-3 ${
                        darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              session.consensus.method === 'cancelled'
                                ? 'bg-red-500/10'
                                : session.consensus.method === 'biometric' 
                                  ? 'bg-green-500/10' 
                                  : 'bg-blue-500/10'
                            }`}>
                              {session.consensus.method === 'cancelled' ? (
                                <XOctagon className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                              ) : (
                                <span className="text-base">
                                  {session.consensus.method === 'biometric' ? '🔐' : '📝'}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className={`text-sm font-medium ${
                                darkMode ? 'text-gray-200' : 'text-gray-900'
                              }`}>
                                {session.consensus.method === 'cancelled' 
                                  ? 'Class Cancelled' 
                                  : session.consensus.method === 'biometric' 
                                    ? 'Biometric Attendance' 
                                    : 'Manual Attendance'
                                }
                              </div>
                              {session.consensus.method !== 'cancelled' && (
                                <div className={`text-xs ${
                                  darkMode ? 'text-gray-500' : 'text-gray-600'
                                }`}>
                                  {session.consensus.timeFrom} - {session.consensus.timeTo}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {session.votes && session.votes.length > 0 
                                ? `${session.votes.length} ${session.votes.length === 1 ? 'vote' : 'votes'}`
                                : session.isPreviousConsensus && 'from last class'
                              }
                            </div>
                            {session.isPreviousConsensus && !hasVotes && (
                              <div className={`text-xs mt-0.5 ${
                                darkMode ? 'text-yellow-400' : 'text-yellow-600'
                              }`}>
                                Not voted today
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* VOTES LIST */}
                    {hasVotes && (
                      <div className="mb-3">
                        <div className={`text-xs font-medium mb-2 ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Reported by ({session.votes.length})
                        </div>
                        <div className="space-y-2">
                          {session.votes.map((vote, idx) => (
                            <div
                              key={idx}
                              className={`flex items-center justify-between p-2 rounded-lg ${
                                darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img
                                  src={getSafePhotoURL(vote?.photoURL, vote?.userName)}
                                  alt={vote?.userName || 'User'}
                                  className="w-6 h-6 rounded-full"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className={`text-sm font-medium truncate ${
                                    darkMode ? 'text-gray-200' : 'text-gray-900'
                                  }`}>
                                    {vote.userName}
                                  </div>
                                  <div className={`text-xs truncate ${
                                    darkMode ? 'text-gray-500' : 'text-gray-600'
                                  }`}>
                                    {vote.method === 'cancelled' 
                                      ? '❌ Class Cancelled'
                                      : `${vote.method === 'biometric' ? '🔐 Biometric' : '📝 Manual'} • ${vote.timeFrom}-${vote.timeTo}`
                                    }
                                  </div>
                                </div>
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-500'}`}>
                                {getTimeAgo(vote.timestamp)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ACTION BUTTONS */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedSessionForNotes(session);
                          setShowNotesModal(true);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                          darkMode 
                            ? 'bg-[#0F1419] text-gray-400 hover:text-gray-300 border border-gray-700' 
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span className="hidden sm:inline">Notes</span>
                      </button>

                      {userVote ? (
                        <button
                          onClick={() => handleRemoveVote(session)}
                          className="flex-1 py-2 px-3 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                        >
                          Remove Vote
                        </button>
                      ) : (
                        <button
                          onClick={() => setSelectedSession(session)}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                            darkMode 
                              ? 'bg-indigo-500 hover:bg-indigo-600 text-white' 
                              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          }`}
                        >
                          Vote Now
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* EDIT COURSES BUTTON */}
          <div className={`rounded-lg p-4 mt-4 ${
            darkMode ? 'bg-[#1C2128]' : 'bg-white border border-gray-200'
          }`}>
            <button
              onClick={() => {
                setSelectedCourses(userData.courses);
                setSelectedSemester(userData.semester);
                setShowCourseSetup(true);
              }}
              className={`w-full flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                darkMode 
                  ? 'text-indigo-400 hover:text-indigo-300' 
                  : 'text-indigo-600 hover:text-indigo-700'
              }`}
            >
              <Settings className="w-4 h-4" />
              Edit My Courses & Semester
            </button>
          </div>

          {/* HELP NOTE */}
          <div className={`rounded-lg p-4 mt-4 ${
            darkMode ? 'bg-blue-500/5 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex gap-3">
              <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                darkMode ? 'text-blue-400' : 'text-blue-600'
              }`} />
              <div>
                <h4 className={`text-sm font-medium mb-1 ${
                  darkMode ? 'text-blue-300' : 'text-blue-900'
                }`}>
                  Help Your Classmates
                </h4>
                <p className={`text-xs leading-relaxed ${
                  darkMode ? 'text-blue-400/80' : 'text-blue-700'
                }`}>
                  Report attendance method or cancelled classes. Leave notes to help everyone stay informed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* VOTE MODAL */}
        {/* ============================================ */}
        {selectedSession && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className={`rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto ${
              darkMode ? 'bg-[#1C2128]' : 'bg-white'
            }`}>
              <div className={`p-4 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {selectedSession.courseName}
                    </h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      {selectedSession.professor} • {selectedSession.time}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedSession(null);
                      setVoteData({ method: '', timeFrom: '', timeTo: '', room: '' });
                      setTimeError('');
                    }}
                    className={`p-2 rounded-lg transition-colors ${
                      darkMode 
                        ? 'hover:bg-[#0F1419] text-gray-400' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {(() => {
                  const existingVote = selectedSession.votes?.find(v => v.userId === currentUser.uid);
                  if (existingVote) {
                    return (
                      <div className={`p-3 rounded-lg ${
                        darkMode ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'
                      }`}>
                        <div className={`text-sm font-medium mb-1 ${
                          darkMode ? 'text-blue-300' : 'text-blue-800'
                        }`}>
                          Your current vote:
                        </div>
                        <div className={`text-sm ${
                          darkMode ? 'text-blue-400' : 'text-blue-700'
                        }`}>
                          {existingVote.method === 'cancelled' 
                            ? '❌ Class Cancelled'
                            : `${existingVote.method === 'biometric' ? '🔐 Biometric' : '📝 Manual'} • ${existingVote.timeFrom}-${existingVote.timeTo}`
                          }
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Select Option *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setVoteData({ ...voteData, method: 'biometric' })}
                      className={`p-3 rounded-lg border font-medium transition-colors ${
                        voteData.method === 'biometric'
                          ? darkMode
                            ? 'bg-green-500/10 border-green-500 text-green-400'
                            : 'bg-green-50 border-green-500 text-green-700'
                          : darkMode
                            ? 'bg-[#0F1419] border-gray-700 text-gray-400 hover:border-gray-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-xl mb-1">🔐</div>
                      <div className="text-xs">Biometric</div>
                    </button>
                    <button
                      onClick={() => setVoteData({ ...voteData, method: 'manual' })}
                      className={`p-3 rounded-lg border font-medium transition-colors ${
                        voteData.method === 'manual'
                          ? darkMode
                            ? 'bg-blue-500/10 border-blue-500 text-blue-400'
                            : 'bg-blue-50 border-blue-500 text-blue-700'
                          : darkMode
                            ? 'bg-[#0F1419] border-gray-700 text-gray-400 hover:border-gray-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-xl mb-1">📝</div>
                      <div className="text-xs">Manual</div>
                    </button>
                    <button
                      onClick={() => setVoteData({ ...voteData, method: 'cancelled', timeFrom: '', timeTo: '' })}
                      className={`p-3 rounded-lg border font-medium transition-colors ${
                        voteData.method === 'cancelled'
                          ? darkMode
                            ? 'bg-red-500/10 border-red-500 text-red-400'
                            : 'bg-red-50 border-red-500 text-red-700'
                          : darkMode
                            ? 'bg-[#0F1419] border-gray-700 text-gray-400 hover:border-gray-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-xl mb-1">❌</div>
                      <div className="text-xs">Cancelled</div>
                    </button>
                  </div>
                </div>

                {voteData.method && voteData.method !== 'cancelled' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          From Time *
                        </label>
                        <input
                          type="time"
                          value={voteData.timeFrom}
                          onChange={(e) => {
                            setVoteData({ ...voteData, timeFrom: e.target.value });
                            validateTime(e.target.value, voteData.timeTo);
                          }}
                          className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                            darkMode 
                              ? 'bg-[#0F1419] border-gray-700 text-gray-300 focus:border-indigo-500' 
                              : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-500'
                          } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          To Time *
                        </label>
                        <input
                          type="time"
                          value={voteData.timeTo}
                          onChange={(e) => {
                            setVoteData({ ...voteData, timeTo: e.target.value });
                            validateTime(voteData.timeFrom, e.target.value);
                          }}
                          className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                            darkMode 
                              ? 'bg-[#0F1419] border-gray-700 text-gray-300 focus:border-indigo-500' 
                              : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-500'
                          } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                        />
                      </div>
                    </div>

                    {timeError && (
                      <div className={`p-2 rounded-lg flex items-center gap-2 ${
                        darkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'
                      }`}>
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs">{timeError}</span>
                      </div>
                    )}

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Room Number (Optional)
                      </label>
                      <input
                        type="text"
                        value={voteData.room}
                        onChange={(e) => setVoteData({ ...voteData, room: e.target.value })}
                        placeholder={selectedSession.room}
                        className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                          darkMode 
                            ? 'bg-[#0F1419] border-gray-700 text-gray-300 placeholder-gray-600 focus:border-indigo-500' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-indigo-500'
                        } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                      />
                    </div>
                  </>
                )}

                <div className={`p-3 rounded-lg border ${
                  darkMode ? 'bg-purple-500/5 border-purple-500/20' : 'bg-purple-50 border-purple-200'
                }`}>
                  <div className="flex gap-2">
                    <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      darkMode ? 'text-purple-400' : 'text-purple-600'
                    }`} />
                    <div>
                      <div className={`text-sm font-medium mb-1 ${
                        darkMode ? 'text-purple-300' : 'text-purple-800'
                      }`}>
                        Transparency Notice
                      </div>
                      <div className={`text-xs leading-relaxed ${
                        darkMode ? 'text-purple-400/80' : 'text-purple-700'
                      }`}>
                        Your vote will be visible to all students along with your name.
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleVoteSubmit}
                  disabled={!voteData.method || (voteData.method !== 'cancelled' && (!voteData.timeFrom || !voteData.timeTo || timeError))}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
                    darkMode 
                      ? 'bg-indigo-500 hover:bg-indigo-600 text-white disabled:bg-gray-700 disabled:text-gray-500' 
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-200 disabled:text-gray-400'
                  } disabled:cursor-not-allowed`}
                >
                  Submit Vote
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* NOTES MODAL */}
        {/* ============================================ */}
        {showNotesModal && selectedSessionForNotes && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className={`rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col ${
              darkMode ? 'bg-[#1C2128]' : 'bg-white'
            }`}>
              <div className={`p-4 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      Notes for {selectedSessionForNotes.courseName}
                    </h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Shared notes for all students
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowNotesModal(false);
                      setSelectedSessionForNotes(null);
                      setNewNote('');
                    }}
                    className={`p-2 rounded-lg transition-colors ${
                      darkMode 
                        ? 'hover:bg-[#0F1419] text-gray-400' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {notes.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className={`w-12 h-12 mx-auto mb-2 ${
                      darkMode ? 'text-gray-700' : 'text-gray-300'
                    }`} />
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      No notes yet. Be the first to add one!
                    </p>
                  </div>
                ) : (
                  notes.map(note => (
                    <div
                      key={note.id}
                      className={`p-3 rounded-lg ${
                        darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <img
                            src={getSafePhotoURL(note?.userPhoto, note?.userName)}
                            alt={note?.userName || 'User'}
                            className="w-6 h-6 rounded-full"
                          />
                          <div>
                            <div className={`text-sm font-medium ${
                              darkMode ? 'text-gray-200' : 'text-gray-900'
                            }`}>
                              {note.userName}
                            </div>
                            <div className={`text-xs ${
                              darkMode ? 'text-gray-600' : 'text-gray-500'
                            }`}>
                              {getTimeAgo(note.createdAt)}
                            </div>
                          </div>
                        </div>
                        {note.userId === currentUser.uid && (
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className={`p-1 rounded transition-colors ${
                              darkMode 
                                ? 'text-red-400 hover:bg-red-500/10' 
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className={`text-sm leading-relaxed ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {note.note}
                      </p>
                    </div>
                  ))
                )}
              </div>

              <div className={`p-4 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                    placeholder="Add a note..."
                    className={`flex-1 py-2 px-3 rounded-lg border transition-colors ${
                      darkMode 
                        ? 'bg-[#0F1419] border-gray-700 text-gray-300 placeholder-gray-600 focus:border-indigo-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-indigo-500'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    className={`p-2 rounded-lg transition-colors ${
                      darkMode 
                        ? 'bg-indigo-500 hover:bg-indigo-600 text-white disabled:bg-gray-700 disabled:text-gray-500' 
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-200 disabled:text-gray-400'
                    } disabled:cursor-not-allowed`}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* REPORT EXTRA CLASS MODAL */}
        {/* ============================================ */}
        {showReportExtraClass && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className={`rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto ${
              darkMode ? 'bg-[#1C2128]' : 'bg-white'
            }`}>
              <div className={`p-4 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      Report Extra Class
                    </h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Admin will review and approve
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowReportExtraClass(false);
                      setExtraClassData({ courseCode: '', courseName: '', professor: '', time: '', room: '' });
                    }}
                    className={`p-2 rounded-lg transition-colors ${
                      darkMode 
                        ? 'hover:bg-[#0F1419] text-gray-400' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Select Course *
                  </label>
                  <select
                    value={extraClassData.courseCode}
                    onChange={(e) => {
                      const courseCode = e.target.value;
                      if (courseCode) {
                        const course = AVAILABLE_COURSES.find(c => c.code === courseCode);
                        if (course) {
                          setExtraClassData({
                            ...extraClassData,
                            courseCode: course.code,
                            courseName: course.name,
                            professor: course.professor
                          });
                        }
                      } else {
                        setExtraClassData({
                          ...extraClassData,
                          courseCode: '',
                          courseName: '',
                          professor: ''
                        });
                      }
                    }}
                    className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                      darkMode 
                        ? 'bg-[#0F1419] border-gray-700 text-gray-300 focus:border-indigo-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-500'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                  >
                    <option value="">Select a course</option>
                    {userData.courses.map(courseCode => {
                      const course = AVAILABLE_COURSES.find(c => c.code === courseCode);
                      return course ? (
                        <option key={course.code} value={course.code}>
                          {course.code} - {course.name}
                        </option>
                      ) : null;
                    })}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Professor (Optional)
                  </label>
                  <input
                    type="text"
                    value={extraClassData.professor}
                    onChange={(e) => setExtraClassData({ ...extraClassData, professor: e.target.value })}
                    placeholder="e.g., Prof. Smith"
                    className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                      darkMode 
                        ? 'bg-[#0F1419] border-gray-700 text-gray-300 placeholder-gray-600 focus:border-indigo-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-indigo-500'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Time *
                  </label>
                  <input
                    type="time"
                    value={extraClassData.time}
                    onChange={(e) => setExtraClassData({ ...extraClassData, time: e.target.value })}
                    className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                      darkMode 
                        ? 'bg-[#0F1419] border-gray-700 text-gray-300 focus:border-indigo-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-500'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Room Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={extraClassData.room}
                    onChange={(e) => setExtraClassData({ ...extraClassData, room: e.target.value })}
                    placeholder="e.g., A-301"
                    className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                      darkMode 
                        ? 'bg-[#0F1419] border-gray-700 text-gray-300 placeholder-gray-600 focus:border-indigo-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-indigo-500'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                  />
                </div>

                <div className={`p-3 rounded-lg border ${
                  darkMode ? 'bg-orange-500/5 border-orange-500/20' : 'bg-orange-50 border-orange-200'
                }`}>
                  <div className="flex gap-2">
                    <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      darkMode ? 'text-orange-400' : 'text-orange-600'
                    }`} />
                    <div>
                      <div className={`text-sm font-medium mb-1 ${
                        darkMode ? 'text-orange-300' : 'text-orange-800'
                      }`}>
                        Important
                      </div>
                      <div className={`text-xs leading-relaxed ${
                        darkMode ? 'text-orange-400/80' : 'text-orange-700'
                      }`}>
                        This extra class will only be visible to students enrolled in this course.
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleReportExtraClass}
                  disabled={!extraClassData.courseCode || !extraClassData.time}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
                    darkMode 
                      ? 'bg-indigo-500 hover:bg-indigo-600 text-white disabled:bg-gray-700 disabled:text-gray-500' 
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-200 disabled:text-gray-400'
                  } disabled:cursor-not-allowed`}
                >
                  Submit for Review
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* PAYMENT MODAL */}
        {/* ============================================ */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className={`rounded-lg max-w-lg w-full ${
              darkMode ? 'bg-[#1C2128]' : 'bg-white'
            }`}>
              <div className={`p-4 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      Subscription Status
                    </h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Manage your subscription
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className={`p-2 rounded-lg transition-colors ${
                      darkMode 
                        ? 'hover:bg-[#0F1419] text-gray-400' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4">
                <div className={`rounded-lg p-4 mb-4 ${
                  darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={getSafePhotoURL(userData?.photoURL, userData?.name)}
                      alt={userData?.name || 'User'}
                      className="w-10 h-10 rounded-full"
                    />
                    <div className="flex-1">
                      <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                        {userData.name}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                        {userData.semester}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Status:</span>
                      <span className={`font-medium ${
                        userData.subscriptionStatus === 'active' 
                          ? 'text-green-500' 
                          : darkMode ? 'text-yellow-400' : 'text-yellow-600'
                      }`}>
                        {userData.subscriptionStatus === 'trial' ? 'Trial Active' : 'Active'}
                      </span>
                    </div>
                    {userData.subscriptionExpiry && (
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Expires:</span>
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          {userData.subscriptionExpiry}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`rounded-lg p-4 mb-4 text-center ${
                  darkMode ? 'bg-[#0F1419]' : 'bg-gray-50'
                }`}>
                  <div className={`text-3xl font-semibold mb-1 ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    ₹{PRICE_PER_SEMESTER}
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    per semester period
                  </div>
                </div>

                <div className="space-y-2">
                  {SEMESTER_PERIODS.map(period => (
                    <button
                      key={period.id}
                      onClick={() => handlePayment(period)}
                      className={`w-full py-2.5 px-4 rounded-lg border font-medium transition-colors text-left flex items-center justify-between ${
                        darkMode 
                          ? 'bg-[#0F1419] border-gray-700 text-gray-300 hover:border-gray-600' 
                          : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-sm">{period.label}</span>
                      <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>→</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default AttendanceTracker;
